import React, { useEffect, useState } from "react";
import { useAuth } from "@/admin/AuthContext";
import { Loader2, Plus, Pencil, Trash2, X, Save, ShieldCheck, User as UserIcon, Activity } from "lucide-react";
import { toast } from "sonner";

const ROLE_BADGE = {
  superadmin: {
    label: "Superadmin",
    className: "bg-purple-100 text-purple-800 border-purple-200",
    icon: ShieldCheck,
  },
  admin: {
    label: "Admin",
    className: "bg-blue-100 text-blue-800 border-blue-200",
    icon: UserIcon,
  },
  siperkasa: {
    label: "Operator SIPERKASA",
    className: "bg-sky-100 text-sky-800 border-sky-200",
    icon: Activity,
  },
};

const EMPTY_FORM = { email: "", name: "", role: "admin", password: "", can_access_siperkasa: true };

const UsersAdminPanel = () => {
  const { authAxios, user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // null = create; object = edit
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const isSuperadmin = user && user.role === "superadmin";

  const load = () => {
    setLoading(true);
    authAxios
      .get("/admin/users")
      .then((r) => setItems(r.data || []))
      .catch((e) =>
        toast.error(e?.response?.data?.detail || "Gagal memuat daftar user")
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isSuperadmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperadmin]);

  if (!isSuperadmin) {
    return (
      <div
        data-testid="admin-users-forbidden"
        className="p-8 rounded-3xl bg-amber-50 border border-amber-200 text-amber-800"
      >
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="h-5 w-5" />
          <div className="font-heading font-semibold">Akses Terbatas</div>
        </div>
        <div className="text-sm">
          Manajemen User Admin hanya dapat diakses oleh <b>superadmin</b>.
        </div>
      </div>
    );
  }

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email,
      name: u.name || "",
      role: u.role || "admin",
      password: "",
      can_access_siperkasa: u.can_access_siperkasa !== false,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const body = { name: form.name, role: form.role, can_access_siperkasa: form.can_access_siperkasa };
        if (form.password) body.password = form.password;
        await authAxios.put(`/admin/users/${editing.id}`, body);
        toast.success("User berhasil diperbarui");
      } else {
        await authAxios.post("/admin/users", {
          email: form.email,
          password: form.password,
          name: form.name,
          role: form.role,
          can_access_siperkasa: form.can_access_siperkasa,
        });
        toast.success("User baru berhasil dibuat");
      }
      closeForm();
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Gagal menyimpan user");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (u.id === user.id) {
      toast.error("Anda tidak dapat menghapus akun sendiri");
      return;
    }
    if (!window.confirm(`Hapus user ${u.email}?`)) return;
    try {
      await authAxios.delete(`/admin/users/${u.id}`);
      toast.success("User berhasil dihapus");
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Gagal menghapus user");
    }
  };

  return (
    <div data-testid="admin-panel-users">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">
            Manajemen User Admin
          </h2>
          <p className="text-sm text-slate-500">
            Buat & kelola akun admin serta superadmin website.
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="admin-user-create-btn"
          className="inline-flex items-center gap-2 bg-blue-800 hover:bg-blue-900 text-white px-4 py-2.5 rounded-full text-sm font-medium shadow-md shadow-blue-900/20 transition"
        >
          <Plus className="h-4 w-4" />
          Tambah User
        </button>
      </div>

      {loading ? (
        <div className="p-8 flex items-center justify-center text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-slate-400 border border-dashed border-slate-200 rounded-2xl">
          Belum ada user admin.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((u) => {
            const badge = ROLE_BADGE[u.role] || ROLE_BADGE.admin;
            const RoleIcon = badge.icon;
            const isSelf = u.id === user.id;
            return (
              <div
                key={u.id}
                data-testid={`admin-user-row-${u.id}`}
                className="bg-white rounded-2xl border border-slate-100 p-5 flex flex-col sm:flex-row sm:items-center gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <div className="font-heading font-semibold text-slate-900 truncate">
                      {u.name || "—"}
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium ${badge.className}`}
                    >
                      <RoleIcon className="h-3 w-3" />
                      {badge.label}
                    </span>
                    {isSelf && (
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider">
                        Akun Anda
                      </span>
                    )}
                    {u.role === "admin" && (
                      <span
                        data-testid={`admin-user-siperkasa-badge-${u.id}`}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium ${
                          u.can_access_siperkasa !== false
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-slate-100 text-slate-500 border-slate-200"
                        }`}
                      >
                        SIPERKASA: {u.can_access_siperkasa !== false ? "Ya" : "Tidak"}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-slate-500 truncate">{u.email}</div>
                  {u.created_at && (
                    <div className="text-xs text-slate-400 mt-1">
                      Dibuat: {new Date(u.created_at).toLocaleString("id-ID")}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => openEdit(u)}
                    data-testid={`admin-user-edit-${u.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 transition"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </button>
                  <button
                    onClick={() => remove(u)}
                    disabled={isSelf}
                    data-testid={`admin-user-delete-${u.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-red-50 hover:bg-red-100 text-red-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Hapus
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          data-testid="admin-user-form-modal"
        >
          <form
            onSubmit={submit}
            className="w-full max-w-md bg-white rounded-3xl shadow-2xl border border-slate-100 p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-lg font-bold text-slate-900">
                {editing ? "Edit User" : "Tambah User Baru"}
              </h3>
              <button
                type="button"
                onClick={closeForm}
                data-testid="admin-user-form-close"
                className="p-1.5 rounded-full hover:bg-slate-100"
              >
                <X className="h-4 w-4 text-slate-500" />
              </button>
            </div>

            <label className="text-xs font-semibold text-slate-600 mb-1.5 block">
              Email {editing && <span className="text-slate-400 font-normal">(tidak dapat diubah)</span>}
            </label>
            <input
              type="email"
              required
              disabled={!!editing}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="admin-user-form-email"
              placeholder="admin@rsbhayangkaratarakan.id"
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm mb-4 disabled:bg-slate-50 disabled:text-slate-500"
            />

            <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Nama</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="admin-user-form-name"
              placeholder="Nama Lengkap"
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm mb-4"
            />

            <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Role</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="admin-user-form-role"
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm mb-4 bg-white"
            >
              <option value="admin">Admin — akses CMS terbatas</option>
              <option value="superadmin">Superadmin — akses penuh + info SIRS</option>
              <option value="siperkasa">Operator SIPERKASA — hanya SIPERKASA</option>
            </select>

            {form.role === "admin" ? (
              <label
                data-testid="admin-user-form-siperkasa"
                className="flex items-start gap-3 mb-4 p-3 rounded-xl border border-slate-200 bg-slate-50 cursor-pointer hover:bg-slate-100 transition"
              >
                <input
                  type="checkbox"
                  checked={!!form.can_access_siperkasa}
                  onChange={(e) => setForm({ ...form, can_access_siperkasa: e.target.checked })}
                  data-testid="admin-user-form-siperkasa-checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-800 focus:ring-blue-100"
                />
                <span className="text-sm text-slate-700">
                  <span className="font-medium">Boleh akses SIPERKASA</span>
                  <span className="block text-xs text-slate-500">
                    Izinkan admin ini membuka menu Sistem Informasi Pengukuran Kinerja RS.
                  </span>
                </span>
              </label>
            ) : form.role === "siperkasa" ? (
              <div className="mb-4 p-3 rounded-xl border border-sky-100 bg-sky-50 text-xs text-sky-700">
                Operator SIPERKASA hanya dapat membuka menu SIPERKASA — tidak memiliki
                akses ke Admin Panel.
              </div>
            ) : (
              <div className="mb-4 p-3 rounded-xl border border-purple-100 bg-purple-50 text-xs text-purple-700">
                Superadmin otomatis memiliki akses penuh ke SIPERKASA &amp; seluruh menu.
              </div>
            )}

            <label className="text-xs font-semibold text-slate-600 mb-1.5 block">
              Password{" "}
              {editing && (
                <span className="text-slate-400 font-normal">(kosongkan jika tidak ingin ubah)</span>
              )}
            </label>
            <input
              type="password"
              required={!editing}
              minLength={6}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="admin-user-form-password"
              placeholder={editing ? "•••••• (opsional)" : "Minimal 6 karakter"}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm mb-6"
            />

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeForm}
                className="px-4 py-2 rounded-full text-sm font-medium text-slate-600 hover:bg-slate-100"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="admin-user-form-submit"
                className="inline-flex items-center gap-2 bg-blue-800 hover:bg-blue-900 disabled:opacity-60 text-white px-5 py-2 rounded-full text-sm font-medium shadow-md shadow-blue-900/20 transition"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Simpan
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default UsersAdminPanel;
